import i18n from 'i18next';
// eslint-disable-next-line @spaced-out/i18n/no-react-i18next-import
import {initReactI18next} from 'react-i18next';
import Backend from 'i18next-chained-backend';
import LocalStorageBackend from 'i18next-localstorage-backend';
import HttpApi from 'i18next-http-backend';

// function to determine if caching should be disabled
const shouldCacheLanguage = () => {
  const pathname = window.location.pathname;
  // define paths where caching should be disabled
  const noCachePaths = ['/scheduler'];
  // check if current path starts with any of the no-cache paths
  return !noCachePaths.some((path) => pathname.startsWith(path));
};

// in-memory fallback for language storage
let languageFallback = 'en';

// cache options for local storage backend
const cacheOptions = {
  enabled: true,
  prefix: 'sense-label-i18next-',
  expirationTime: 6 * 60 * 60 * 1000, // 6 hours
  defaultVersion: 'v1.1',
  versions: {en: 'v1.1'},
  store: typeof window !== 'undefined' ? window.localStorage : null,
};

// function to construct API load path
const getLoadPath = (lng, ns) =>
  '/api/v1/configurations/label?lng=' + lng + '&ns=' + ns;

const httpApiOptions = {
  loadPath: getLoadPath('en', 'translations'),
  parse(data) {
    return JSON.parse(data);
  },
  requestOptions: {
    mode: 'cors',
    credentials: 'include',
    cache: 'default',
  },
};

// dynamically read available languages from local JSON files
const getAvailableLanguages = () => {
  try {
    const context = require.context('./translations', false, /\.json$/);

    const languages = new Set(
      context.keys().map((file) => {
        const match = file.match(/([^/]+)\.json$/);
        return match ? match[1] : null;
      }),
    );

    return Array.from(languages).filter(Boolean);
  } catch (error) {
    console.error('Error fetching available languages:', error);
    return [];
  }
};

// load translations for a specific language
const loadTranslations = async (language) => {
  try {
    const translationData = await import(`./translations/${language}.json`);

    let translations;
    if (
      typeof translationData === 'string' ||
      typeof translationData?.default === 'string'
    ) {
      // Case 1: Webpack resolved to a URL, fetch the JSON
      const response = await fetch(translationData.default || translationData);
      if (!response.ok) {
        throw new Error(
          `Failed to fetch translation for ${language}: ${response.statusText}`,
        );
      }
      translations = await response.json();
    } else {
      // Case 2: Webpack provided a JSON object directly
      translations = translationData.default || translationData;
    }

    return translations;
  } catch (error) {
    console.error(`Error loading translation for ${language}:`, error);
    return null;
  }
};

// Create i18n instance
const labelI18nInstance = i18n.createInstance();

// Initialize i18n instance
let i18nInitialized = false;

export const initializeTranslations = async (agencyDefaultLang) => {
  if (i18nInitialized) {
    return;
  }

  await labelI18nInstance
    .use(Backend)
    .use({
      type: 'languageDetector',
      async: false,
      init: () => {},
      detect: () => {
        if (typeof localStorage !== 'undefined') {
          const storedLanguage = localStorage.getItem('selectedLanguage');
          const agencyLanguage = agencyDefaultLang;

          if (agencyLanguage) {
            localStorage.setItem('selectedLanguage', agencyLanguage);
            return agencyLanguage;
          }

          return storedLanguage || languageFallback;
        }
        return languageFallback;
      },
      cacheUserLanguage: (lng) => {
        if (typeof localStorage !== 'undefined' && shouldCacheLanguage()) {
          localStorage.setItem('selectedLanguage', lng);
        } else {
          languageFallback = lng;
        }
      },
    })
    .use(initReactI18next)
    .init({
      debug: false,
      fallbackLng: 'en',
      backend: {
        backends: [
          LocalStorageBackend, // Local cache backend
          HttpApi, // API backend
        ],
        backendOptions: [cacheOptions, httpApiOptions],
      },
      resources: {},
      defaultNS: 'translation',
      ns: ['translation'],
      interpolation: {
        escapeValue: false,
      },
      react: {
        useSuspense: false,
        wait: false,
      },
    });

  i18nInitialized = true;
  // load the languages after i18n is initialized, & first load the default language
  const defaultLanguage = agencyDefaultLang || 'en';
  // default language is loaded first
  loadLanguageAndTranslationResourceBundle(defaultLanguage);
  // other languages are loaded
  loadOtherLanguages(defaultLanguage);
};

export const loadLanguageAndTranslationResourceBundle = async (language) => {
  const translations = await loadTranslations(language);
  labelI18nInstance.addResourceBundle(language, 'translation', translations);
};

export const loadOtherLanguages = async (defaultLanguage) => {
  const languages = getAvailableLanguages();
  for (const language of languages) {
    if (language !== defaultLanguage) {
      await loadLanguageAndTranslationResourceBundle(language);
    }
  }
};

export const ensureInitialized = async (agencyLanguage) => {
  if (!i18nInitialized) {
    await initializeTranslations(agencyLanguage);
  }
};

export default labelI18nInstance;
